import { initializeApp } from 'firebase/app'
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check'
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth'
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  type Firestore,
} from 'firebase/firestore'
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions'
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

// No real Firebase project is configured in most dev environments yet (no
// .env.local — see .env.example). getAuth()/getFirestore() throw
// synchronously on a missing/invalid apiKey, and this module is imported
// transitively by the router guard on every page load, so an unguarded call
// here would crash the entire app to a blank screen before Vue even mounts.
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey)

export const app = isFirebaseConfigured ? initializeApp(firebaseConfig) : null

// App Check stops non-app clients (e.g. a copied Firebase config) from
// calling Firestore/Functions directly — required alongside firestore.rules,
// not instead of them.
//
// Enforcement is per-service and is NOT all on. Every callable enforces it in
// code (`onCall({ enforceAppCheck: true })`, all 40 of them), but the
// console-side enforcement for Firestore, Storage and Identity Toolkit was
// still UNENFORCED when checked on 2026-09-18 — so a direct Firestore read
// with a copied config is stopped by firestore.rules alone. Turning those on
// is a console change, and worth doing deliberately: a stale tab without a
// token starts failing the moment it flips.
const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_V3_SITE_KEY
if (app && recaptchaSiteKey) {
  if (import.meta.env.DEV) {
    // https://firebase.google.com/docs/app-check/web/debug-provider
    // `||`, not `??` — an unset .env value comes through as `""`, not
    // `undefined`, so `?? true` never actually falls back and this was
    // silently sending an empty debug token to Google's API, which
    // rejects it outright. That failure made App Check never produce a
    // token, which made every App Check-enforced Cloud Function call
    // (checkLoginLock/recordFailedLogin/resetFailedLogins) fail with 401
    // before the real request logic ever ran — surfacing to users as a
    // generic "Incorrect credentials" on login, regardless of password.
    ;(self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      import.meta.env.VITE_APPCHECK_DEBUG_TOKEN || true
  }
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(recaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  })
}

if (!isFirebaseConfigured && import.meta.env.DEV) {
  console.warn(
    '[firebase] VITE_FIREBASE_* env vars are missing — running without a backend. ' +
      'Copy .env.example to .env.local and fill in real values to enable auth/Firestore.',
  )
}

export const auth: Auth | null = app ? getAuth(app) : null

// Persistent IndexedDB cache gives direct Firestore writes (courts.ts's
// check-in/court-assignment setDoc/updateDoc/writeBatch calls) offline
// resilience for free — a write made while offline queues locally and
// flushes automatically on reconnect, no custom retry code needed. Cloud
// Function callables (recordSale, checkInBooking, etc.) don't benefit from
// this — those go through the offline store's own queue instead (see
// stores/offline.ts). Multi-tab manager avoids two open tabs (e.g. Court
// Assignment + Item Sales on separate browser tabs) fighting over the
// same local cache lock.
export const db: Firestore | null = app
  ? initializeFirestore(app, {
      localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
    })
  : null
export const functions: Functions | null = app ? getFunctions(app) : null
export const storage: FirebaseStorage | null = app ? getStorage(app) : null

// TEMPORARY — pre-launch manual QA against local emulators only, not
// committed. Remove before merging.
if (app && import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  if (auth) connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  if (db) connectFirestoreEmulator(db, '127.0.0.1', 8080)
  if (functions) connectFunctionsEmulator(functions, '127.0.0.1', 5001)
  if (storage) connectStorageEmulator(storage, '127.0.0.1', 9199)
}
